<template>
  <div v-click-away="outsideClick" class="inline-block">
    <button
      ref="country"
      type="button"
      aria-label="flag"
      data-test-id="button-flag"
      class="country-button flex items-center pr-4 focus:outline-none"
      :class="{'cursor-default': !enableClick}"
      @click.prevent="enableClick && showHideDropdown()"
    >
      <span class="h-6 w-6"><MajorityUiMajorityFlag :iso="flagISO" class="h-6 w-6" :class="{ 'bg-middle-light': squareFlags }" :is-square="squareFlags" /></span>
      <span v-if="showCaret && !alternativeCaret" class="text-dark caret" />
      <img
        v-else-if="showCaret && alternativeCaret"
        src="/svg/caret-down.svg"
        aria-hidden
        alt=""
        class="ml-1"
        :class="{ 'transform rotate-180': showDropdown }"
      >
    </button>
    <transition
      name="expand"
      @enter="enter"
      @after-enter="afterEnter"
      @leave="leave"
    >
      <div v-show="showDropdown" class="country-dropdown bg-white absolute top-full w-full mt-1 shadow-dropdown z-50 flex-col">
        <input
          ref="countrySearchInput"
          type="text"
          :placeholder="placeholder"
          class="form-input-v2 bg-transparent mx-2 my-1"
          data-test-id="input-phone-input-phone"
          :value="countrySearch"
          @input="onInput"
        >
        <ul
          ref="dropdownList"
          class="country-list"
        >
          <li
            v-for="(country, index) in getAllSearchedCountries"
            :key="index"
            class="hover:bg-light-green"
          >
            <button type="button" :data-test-id="`button-country-${country.id}`" class="flex justify-between w-full px-2 py-1 focus:outline-none" @click.stop="onSelectCountry(country.id)">
              <span class="flex text-left mr-1">
                <span class="mr-1 h-6 w-6"><MajorityUiMajorityFlag :iso="country.id" class="h-6 w-6 bg-middle-light" is-square /></span>
                {{ country.name }}
              </span>
              <p v-if="showCallingCodes" class="m-0">
                {{ country.callingCode }}
              </p>
            </button>
          </li>
        </ul>
        <div v-if="showFadeEffect" class="fade-effect absolute w-full bottom-0" />
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { mixin as VueClickAway } from 'vue3-click-away'
import { applicationDataStore } from '~/store/application/data'
export default {
  mixins: [VueClickAway],
  model: {
    event: 'selected'
  },
  props: {
    value: {
      type: String,
      default: () => 'US'
    },
    popular: {
      type: Array,
      default: () => []
    },
    sorted: {
      type: Array,
      default: () => []
    },
    squareFlags: {
      type: Boolean,
      default: true
    },
    showCaret: {
      type: Boolean,
      default: true
    },
    showCallingCodes: {
      type: Boolean,
      default: true
    },
    alternativeCaret: {
      type: Boolean,
      default: false
    },
    enableClick: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      showDropdown: false,
      countrySearch: '',
      placeholder: this.$messages('label.search'),
      displayFadeEffect: false,
      flagISO: 'US'
    }
  },
  computed: {
    ...mapState(applicationDataStore, {
      getAllCountries: 'getAllCountries',
      getCountry: 'getCountry',
      searchCountry: 'searchCountry',
      getAllCountriesSorted: 'getAllCountriesSorted'
    }),
    getAllSearchedCountries () {
      return this.getSearchedCountries.length > 0 ? this.getSearchedCountries : this.getPopularCountries
    },
    getPopularCountries () {
      const countries = []

      for (const country of this.popular) {
        countries.push(this.getAllCountries[country])
      }

      return countries
    },
    getSearchedCountries () {
      const allCountries = []
      if (!this.sorted.length) {
        for (const country in this.getAllCountries) {
          allCountries.push(this.getAllCountries[country])
        }
      } else {
        allCountries.push(...this.getAllCountriesSorted(this.sorted))
      }

      if (!this.countrySearch) {
        return allCountries
      }

      const startsWithCountries = allCountries.filter(x => x.name.toLowerCase().startsWith(this.countrySearch.toLowerCase()))
      const startsWithCallingCodeCountries = allCountries.filter(x => x.callingCode.toLowerCase().replace('+', '').startsWith(this.countrySearch.toLowerCase().replace('+', ''))).sort(this.sortByCallingCode)
      const includeCallingCodeCountries = allCountries.filter(x => x.callingCode.toLowerCase().replace('+', '').includes(this.countrySearch.toLowerCase().replace('+', ''))).sort(this.sortByCallingCode)
      const includeCountries = allCountries.filter(x => x.name.toLowerCase().includes(this.countrySearch.toLowerCase()))

      const finalCountries = [...new Set([...startsWithCountries, ...startsWithCallingCodeCountries, ...includeCallingCodeCountries, ...includeCountries])]
      return finalCountries
    },
    showFadeEffect () {
      return this.displayFadeEffect
    }
  },
  created () {
    this.flagISO = this.value || 'US'
  },
  mounted () {
    this.$refs.dropdownList.addEventListener('scroll', this.handleFade)
  },
  beforeUnmount () {
    if (this.$refs.dropdownList) {
      this.$refs.dropdownList.removeEventListener('scroll', this.handleFade)
    }
  },
  methods: {
    onInput (val) {
      this.countrySearch = val.target.value

      this.$track.viewSearchResults('PhoneInputCountryPicker', this.getSearchedCountries.length > 0, { searchQuery: this.countrySearch })
      this.$nextTick(() => {
        this.handleFade({ target: this.$refs.dropdownList })
      })
    },
    outsideClick () {
      this.showDropdown = false

      if (this.getSearchedCountries.length === 0) {
        this.countrySearch = ''
      }
    },
    showHideDropdown () {
      this.showDropdown = !this.showDropdown
      if (this.showDropdown) {
        this.$nextTick(() => {
          this.$refs.countrySearchInput.focus()
          this.handleFade({ target: this.$refs.dropdownList })
        })
      }
    },
    onSelectCountry (countryId) {
      this.$track.searchResultSelected('PhoneInputCountryPicker', countryId, { searchQuery: this.countrySearch })
      this.$emit('selected', countryId)
      this.flagISO = countryId
      this.showDropdown = false
      this.countrySearch = ''
    },
    sortByCallingCode (a, b) {
      const item = Number(a.callingCode.replace('+', ''))
      const item2 = Number(b.callingCode.replace('+', ''))

      if (isNaN(item) || isNaN(item2)) { return 0 }

      return item - item2
    },
    handleFade (event) {
      this.displayFadeEffect = !this.isEndOfScroll(event.target)
    },
    isEndOfScroll (target) {
      const currentScroll = target.scrollTop
      const scrollLimit = target.scrollHeight - target.offsetHeight

      return currentScroll >= scrollLimit
    },
    enter (el) {
      el.style.height = 'auto'
      const height = getComputedStyle(el).height
      el.style.height = 0
      getComputedStyle(el)
      setTimeout(() => {
        el.style.height = height
      })
    },
    afterEnter (el) {
      el.style.height = 'auto'
    },
    leave (el) {
      el.style.height = getComputedStyle(el).height

      getComputedStyle(el)

      setTimeout(() => {
        el.style.height = 0
      })
    }
  }
}
</script>

<style lang="postcss" scoped>
.country-button {
  height: 1.5rem;
}

.country-dropdown {
  display: flex;
}

.country-list {
  max-height: 130px;
  overflow-y: scroll;
}

.fade-effect {
  height: 15%;
  pointer-events: none;
  background: linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 1) 81%)
}

@keyframes slideaway {
  from { display: none; }
  to { transform: translateY(-40px); opacity: 0;}
}

.show-dropdown {
  animation: slideaway 200ms;
  display: flex;
}

.caret {
  @apply top-0 ml-3 mb-1;
}
</style>
